/*
 * File: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice\src\Content\Modals\ModalUser\ModalUser.tsx
 * Project: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice
 * Created Date: Friday July 7th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Tuesday, 28th November 2023 12:51:56
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
*/

import { MouseEventHandler, useEffect, useMemo, useRef, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { Form, Button, Col } from "react-bootstrap";
import MyModal from "Content/Modals/MyModal";
import translator from "MultiLanguage/TranslationWrapper";
import Styles from "Content/Modals/ModalUser/ModalUser.module.css";
import LoadingScreen from "Content/LoadingScreen/LoadingScreen";
import ModalConfirmation from "Content/Modals/ModalConfirmation/ModalConfirmation";
import UserForm from "Content/Contact/UserForm/UserForm";
import CompanyForm from "Content/Contact/CompanyForm/CompanyForm";
import UserService from "Services/Users/UserService";
import CompanySelection from "Content/Contact/CompanyForm/CompanySelection";
import PasswordCreateInput from "Content/Password/PasswordCreateInput";
import { CompanyIntel } from "Type/company.type";
import { GroupType } from "Type/group.type";
import {Address, UserCreationIntel, UserIntel} from "Type/user.type";
import AddressContact from "Content/Contact/AddressContact/AddressContact";

type ModalProps = {
    show: boolean,
    onClose: MouseEventHandler<HTMLDivElement>,
    closeModal: MouseEventHandler,
    title: string,
    isClient: boolean
};

function ModalUser(props: ModalProps) {
    const [spinner, setSpinner] = useState(false);
    const [isOkay, setIsOkay] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [roles, setRoles] = useState<string[]>(["ADMIN"]);
    const [role, setRole] = useState<string>(props.isClient ? "CLIENT" : "");
    const sendingGroups = useRef<GroupType[]>([]);
    const [isMyUserSendable, setIsMyUserSendable] = useState(false);
    const [isMyUserCompanySendable, setIsMyUserCompanySendable] = useState(false);
    const userIntelRef = useRef<UserIntel>({});
    const companyIntelRef = useRef<CompanyIntel>({});
    const companyAddressRef = useRef<Address>({
        address: "",
        city: "",
        postalCode: "",
        state: "",
        country: "",
    });
    const passwordRef = useRef<{password?: string}>({});
    const commercialRef = useRef<string>();
    const flyingCommercialRef = useRef<string>();
    const RoleItems = useMemo(() => {
        return roles.map((val) => <MenuItem key={`role-${val}`} value={val}>{translator.decode(`users.roles.${val.toLowerCase()}`)}</MenuItem>);
    }, [roles]);

    const checkUserIsSendable = async () => {
        let wait = await new Promise(resolve => setTimeout(resolve, 1050));
        if (userIntelRef.current && userIntelRef.current.firstName !== undefined && userIntelRef.current.lastName !== undefined && (userIntelRef.current.cellPhone !== undefined || userIntelRef.current.phone !== undefined)) {
            if (role === "CLIENT")
                return setIsMyUserSendable(true);
            else if (passwordRef.current.password !== undefined && passwordRef.current.password !== "")
                return setIsMyUserSendable(true);
        }
        setIsMyUserSendable(false);
    };

    const checkCompanyIsSendable = async () => {
        let wait = await new Promise(resolve => setTimeout(resolve, 750));
        if (role === "CLIENT" && companyIntelRef.current && companyIntelRef.current.Name != undefined && companyIntelRef.current.Phone != undefined && companyAddressRef.current?.postalCode != undefined)
            return setIsMyUserCompanySendable(true);
        else if (role !== "CLIENT" && companyIntelRef.current.id != undefined)
            return setIsMyUserCompanySendable(true);
        setIsMyUserCompanySendable(false);
    };

    useEffect(()=>{
        if (props.show) {
            UserService.getUserRoles().then((items: string[]) => {
                setRoles(items.filter((val) => val !== "CLIENT"));
            }).catch((reason) => {
                console.error(`Unable to load available roles: ${reason.message}`);
            });
        }
    }, [props.show]);

    const HandleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setSpinner(true);
        let myIntels: UserCreationIntel = {
            Groups: sendingGroups.current.map(val => val.id),
            LastName: (userIntelRef.current.lastName)!,
            FirstName: (userIntelRef.current.firstName)!,
            Email: userIntelRef.current.email!,
            Address: companyAddressRef.current?.address,
            City: companyAddressRef.current?.city,
            PostalCode: companyAddressRef.current?.postalCode ?? "31200",
            State: companyAddressRef.current?.state,
            Country: companyAddressRef.current?.country,
            LanguageID: userIntelRef.current.language!,
            Role: role,
            Phone: userIntelRef.current.phone,
            CellPhone: userIntelRef.current.cellPhone,
            Password: undefined,
            Company: {
                id: undefined,
                Name: undefined,
                Country: undefined,
                KBIS: undefined,
                Siren: undefined,
                StandardPhoneNumber: undefined,
                Address: undefined,
                City: undefined,
                PostalCode: undefined,
                State: undefined
            },
        };
        if (role === "CLIENT")
            myIntels = {...myIntels,
                CommercialID: commercialRef.current,
                IDFile: userIntelRef.current.identityFile,
                FlyingCommercialID: flyingCommercialRef.current,
                Company: {
                    Name: companyIntelRef.current.Name,
                    Country: companyAddressRef.current?.country,
                    Siren: companyIntelRef.current.Siren,
                    KBIS: companyIntelRef.current.KBIS,
                    StandardPhoneNumber: companyIntelRef.current.Phone,
                    Address: companyAddressRef.current?.address,
                    City: companyAddressRef.current?.city,
                    PostalCode: companyAddressRef.current!.postalCode,
                    State: companyAddressRef.current?.state
                }}
        else {
            myIntels.Password = passwordRef.current.password;
            myIntels = {...myIntels, Company: {
                id: companyIntelRef.current.id!,
            }};
        }

        UserService.addUser(myIntels).then(res => {
            if (res.status === 200 || res.status === 201) {
                setIsOkay(true);
                setOpenModal(true);
            }
            if (res.status >= 400) {
                setIsOkay(false);
                setOpenModal(true);
            }
            setSpinner(false);
        }).catch((res: any) => {
            setIsOkay(false);
            setOpenModal(true);
            setSpinner(false);
        });
    };

    return(
        <>
            <MyModal closeModal={props.closeModal} show={props.show} addCloseCross={true} onClose={props.onClose} footer={(() => { return (role && role !== "" ? <Button disabled={!(isMyUserSendable && isMyUserCompanySendable)} onClick={(e)=> HandleSubmit(e)}>{translator.decode("dynamic.create")}</Button>: <></>); })()} title={""} className={`${Styles.myModal}`}>
                {
                    spinner ?
                        <div className={`${Styles.spinnerHolder}`}>
                            <LoadingScreen />
                        </div>
                        :null
                }
                <Form className={`${Styles.formHolder}`} onSubmit={HandleSubmit}>
                    <div className={`${Styles.forms}`}>
                        {
                            !props.isClient ?
                            <FormControl className={`${Styles.formfield}`} size="small" sx={{width:"fit-content"}}>
                                <InputLabel id="select-role-label">{translator.decode("users.roles.role")}</InputLabel>
                                    <Select className={`${Styles.select}`} label={translator.decode("users.roles.role")} labelId="select-role-label" value={role} onChange={(e) => setRole(e.target.value!.toString())}>
                                        {RoleItems}
                                    </Select>
                            </FormControl>
                            :null
                        }
                        {role && role !== "" ?
                            <>
                                {role === "CLIENT" ?
                                    <>
                                        <div className="d-flex gap-3 w-100">
                                            <UserForm ref={userIntelRef} onUpdate={checkUserIsSendable} role={role} />
                                            <CompanyForm ref={companyIntelRef} onUpdate={checkCompanyIsSendable} />
                                        </div>
                                        <Form.Group className={`${Styles.formfield}`} >
                                            <Col>
                                                <AddressContact address={{
                                                    houseNumber: "",
                                                    street: "",
                                                    postalCode: "",
                                                    city: "",
                                                    score: 0,
                                                    state: "",
                                                }} ref={companyAddressRef} onUpdate={checkCompanyIsSendable}
                                                />
                                            </Col>
                                        </Form.Group>
                                    </>
                                :
                                <>
                                    <div className="d-flex gap-3">
                                        <UserForm ref={userIntelRef} onUpdate={checkUserIsSendable} role={role} />
                                        <div className={`${Styles.formHolder}`}>
                                            <h3>{translator.decode("dynamic.company")}</h3>
                                            <CompanySelection onUpdate={checkCompanyIsSendable} ref={companyIntelRef} />
                                            <h3>{translator.decode("dynamic.password")}</h3>
                                            <PasswordCreateInput ref={passwordRef} onUpdate={checkUserIsSendable} />
                                        </div>
                                    </div>
                                </>
                                }
                            </>
                        : null}
                    </div>
                </Form>
            </MyModal>
            <ModalConfirmation show={openModal} onClose={() => setOpenModal(false)} closeModal={() => setOpenModal(false)} isOkay={isOkay} />
        </>
    )
}

export default ModalUser;
